import React, { useState } from 'react';


const RiskMatrix = ({ location, category }) => {
  const [hoveredCell, setHoveredCell] = useState(null);

  // BP OMS 8x8 Matrix Structure
  const probability = [
    { level: 'A', label: 'Very Unlikely', description: '< 0.01%' },
    { level: 'B', label: 'Unlikely', description: '0.01% - 0.1%' },
    { level: 'C', label: 'Possible', description: '0.1% - 1%' },
    { level: 'D', label: 'Likely', description: '1% - 10%' },
    { level: 'E', label: 'Very Likely', description: '10% - 50%' },
    { level: 'F', label: 'Expected', description: '50% - 90%' },
    { level: 'G', label: 'Frequent', description: '90% - 99%' },
    { level: 'H', label: 'Continuous', description: '> 99%' },
  ];

  const consequence = [
    { level: '1', label: 'Negligible', description: 'Minor impact' },
    { level: '2', label: 'Minor', description: 'Limited impact' },
    { level: '3', label: 'Moderate', description: 'Moderate impact' },
    { level: '4', label: 'Major', description: 'Significant impact' },
    { level: '5', label: 'Severe', description: 'Severe impact' },
    { level: '6', label: 'Critical', description: 'Critical impact' },
    { level: '7', label: 'Catastrophic', description: 'Catastrophic impact' },
    { level: '8', label: 'Extreme', description: 'Extreme impact' },
  ];

  // Risk level definitions based on probability and consequence
  const getRiskLevel = (probIndex, conseqIndex) => {
    const total = probIndex + conseqIndex;
    if (total <= 2) return 'low';
    if (total <= 4) return 'medium';
    if (total <= 6) return 'high';
    return 'critical';
  };

  const getRiskColor = (level) => {
    const colors = {
      low: 'bg-green-400 hover:bg-green-500',
      medium: 'bg-yellow-400 hover:bg-yellow-500',
      high: 'bg-orange-400 hover:bg-orange-500',
      critical: 'bg-red-500 hover:bg-red-600',
    };
    return colors?.[level];
  };

  // Mock data - number of hazards in each cell
  const hazardCount = {
    'A-1': 5, 'A-2': 8, 'A-3': 3, 'A-4': 2, 'A-5': 1, 'A-6': 0, 'A-7': 0, 'A-8': 0,
    'B-1': 12, 'B-2': 15, 'B-3': 10, 'B-4': 7, 'B-5': 4, 'B-6': 2, 'B-7': 1, 'B-8': 0,
    'C-1': 18, 'C-2': 22, 'C-3': 16, 'C-4': 12, 'C-5': 8, 'C-6': 5, 'C-7': 2, 'C-8': 1,
    'D-1': 14, 'D-2': 19, 'D-3': 15, 'D-4': 11, 'D-5': 9, 'D-6': 6, 'D-7': 3, 'D-8': 2,
    'E-1': 10, 'E-2': 13, 'E-3': 11, 'E-4': 9, 'E-5': 7, 'E-6': 5, 'E-7': 4, 'E-8': 3,
    'F-1': 6, 'F-2': 8, 'F-3': 7, 'F-4': 6, 'F-5': 5, 'F-6': 4, 'F-7': 3, 'F-8': 2,
    'G-1': 3, 'G-2': 4, 'G-3': 4, 'G-4': 3, 'G-5': 3, 'G-6': 2, 'G-7': 2, 'G-8': 1,
    'H-1': 1, 'H-2': 2, 'H-3': 2, 'H-4': 2, 'H-5': 1, 'H-6': 1, 'H-7': 1, 'H-8': 1,
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Matrix Container */}
        <div className="flex">
          {/* Y-Axis Label (Probability) */}
          <div className="flex items-center mr-4">
            <div className="writing-vertical text-sm font-bold text-gray-700">
              PROBABILITY
            </div>
          </div>

          {/* Matrix Grid */}
          <div className="flex-1">
            {/* Consequence Headers */}
            <div className="flex mb-2">
              <div className="w-16"></div>
              {consequence?.map((c) => (
                <div key={c?.level} className="flex-1 text-center px-1">
                  <div className="font-bold text-sm text-gray-900">{c?.level}</div>
                  <div className="text-xs text-gray-600 mt-1">{c?.label}</div>
                </div>
              ))}
            </div>

            {/* Matrix Rows */}
            {probability?.map((p, pIndex) => (
              <div key={p?.level} className="flex mb-1">
                {/* Probability Label */}
                <div className="w-16 flex flex-col justify-center pr-2 text-right">
                  <div className="font-bold text-sm text-gray-900">{p?.level}</div>
                  <div className="text-xs text-gray-600">{p?.label}</div>
                </div>

                {/* Risk Cells */}
                {consequence?.map((c, cIndex) => {
                  const cellId = `${p?.level}-${c?.level}`;
                  const riskLevel = getRiskLevel(pIndex, cIndex);
                  const count = hazardCount?.[cellId] || 0;
                  const isHovered = hoveredCell === cellId;

                  return (
                    <div
                      key={cellId}
                      className={`
                        flex-1 aspect-square flex flex-col items-center justify-center
                        border-2 border-white cursor-pointer transition-all
                        ${getRiskColor(riskLevel)}
                        ${isHovered ? 'scale-105 shadow-lg z-10' : ''}
                      `}
                      onMouseEnter={() => setHoveredCell(cellId)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      <div className="text-white font-bold text-lg">{count}</div>
                      {isHovered && (
                        <div className="text-xs text-white font-medium mt-1">
                          View Details
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* X-Axis Label (Consequence) */}
            <div className="text-center mt-4">
              <div className="text-sm font-bold text-gray-700">CONSEQUENCE</div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-400 rounded"></div>
            <span className="text-sm font-medium text-gray-700">Low Risk</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-yellow-400 rounded"></div>
            <span className="text-sm font-medium text-gray-700">Medium Risk</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-400 rounded"></div>
            <span className="text-sm font-medium text-gray-700">High Risk</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-red-500 rounded"></div>
            <span className="text-sm font-medium text-gray-700">Critical Risk</span>
          </div>
        </div>

        {/* Risk Summary */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-900">87</div>
            <div className="text-sm text-green-700 mt-1">Low Risk Hazards</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-900">156</div>
            <div className="text-sm text-yellow-700 mt-1">Medium Risk Hazards</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-900">63</div>
            <div className="text-sm text-orange-700 mt-1">High Risk Hazards</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-900">18</div>
            <div className="text-sm text-red-700 mt-1">Critical Risk Hazards</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskMatrix;